// Pure data-shaping and rendering for the Work page: a grouped-list view of all
// work — big tasks (plans) and the tasks beneath them — bucketed by lifecycle
// status into collapsible sections. It mirrors the desktop kanban's columns as a
// vertical list, with no routing or connection logic.
import { el } from "../dom.js";
import type { BigTask, Comment, Plan, Task } from "../types.js";

// Combined lifecycle order: big-task stages first (backlog → approved), then the
// per-task run states (queued → rejected), then terminal ones. Unknown statuses
// sort alphabetically after these.
const STATE_ORDER = [
  "backlog",
  "planning",
  "proposed",
  "approved",
  "queued",
  "running",
  "review",
  "blocked",
  "accepted",
  "merged",
  "done",
  "rejected",
  "failed",
];

// Statuses whose section collapses by default — terminal states are reference,
// not the day's focus.
const TERMINAL = new Set(["accepted", "merged", "done", "rejected", "failed"]);

// Status → semantic accent for the section: good (green), bad (red), neutral.
const GOOD = new Set(["approved", "accepted", "merged", "done"]);
const BAD = new Set(["blocked", "rejected", "failed"]);

export type WorkKind = "bigtask" | "task";

export interface WorkItem {
  id: string;
  kind: WorkKind;
  title: string;
  status: string;
  subtitle: string;
  spec?: string;
  intent?: string;
  riskTier?: string;
  agentId?: string;
  branch?: string;
  error?: string;
}

export function tasksFromPlans(plans: Plan[]): Task[] {
  if (!plans || plans.length === 0) return [];
  const seen = new Set<string>();
  const result: Task[] = [];
  for (const plan of plans) {
    for (const task of plan.tasks ?? []) {
      if (!seen.has(task.id)) {
        seen.add(task.id);
        result.push(task);
      }
    }
  }
  return result;
}

// Fold big tasks and their child tasks into one flat list of work items. Big
// tasks come from both the dedicated list and any carried on a plan; tasks come
// from the plans, each tagged with its parent big task for context.
export function buildWorkItems(plans: Plan[], bigTasks: BigTask[]): WorkItem[] {
  const items: WorkItem[] = [];

  const seenBig = new Set<string>();
  const allBig: BigTask[] = [...(bigTasks ?? [])];
  for (const plan of plans ?? []) if (plan.bigTask) allBig.push(plan.bigTask);
  for (const b of allBig) {
    if (!b || seenBig.has(b.id)) continue;
    seenBig.add(b.id);
    items.push({
      id: b.id,
      kind: "bigtask",
      title: b.title,
      status: b.status,
      subtitle: b.error || b.intent || "",
      intent: b.intent || undefined,
      error: b.error || undefined,
    });
  }

  const seenTask = new Set<string>();
  for (const plan of plans ?? []) {
    const parent = plan.bigTask?.title ?? "";
    for (const t of plan.tasks ?? []) {
      if (seenTask.has(t.id)) continue;
      seenTask.add(t.id);
      const bits = [t.riskTier ? `${t.riskTier} risk` : "", parent ? `in ${parent}` : ""].filter(Boolean);
      items.push({
        id: t.id,
        kind: "task",
        title: t.title,
        status: t.status,
        subtitle: bits.join(" · "),
        spec: t.spec || undefined,
        riskTier: t.riskTier || undefined,
        agentId: t.agentId || undefined,
        branch: t.branch || undefined,
      });
    }
  }

  return items;
}

// Drop terminal-status work from the Work page so only active work shows —
// mirrors how Backlog (status==="backlog") and Feed (status==="proposed") filter
// to their own slice. Terminal items live in their own views, not here.
export function activeWorkItems(items: WorkItem[]): WorkItem[] {
  if (!items) return [];
  return items.filter((it) => !TERMINAL.has(it.status));
}

export function groupWorkItems(items: WorkItem[]): { state: string; items: WorkItem[] }[] {
  if (!items || items.length === 0) return [];
  const map = new Map<string, WorkItem[]>();
  for (const it of items) {
    const bucket = map.get(it.status);
    if (bucket) bucket.push(it);
    else map.set(it.status, [it]);
  }
  const known = STATE_ORDER.filter((s) => map.has(s));
  const unknown = [...map.keys()].filter((s) => !STATE_ORDER.includes(s)).sort();
  return [...known, ...unknown].map((state) => ({ state, items: map.get(state)! }));
}

function statusAccent(status: string): string {
  if (GOOD.has(status)) return "good";
  if (BAD.has(status)) return "bad";
  return "";
}

type ApiFetch = <T>(method: string, path: string) => Promise<T>;

function buildDetailBody(it: WorkItem): HTMLElement {
  const children: (Node | string)[] = [];

  if (it.kind === "task") {
    if (it.spec) children.push(el("p", { class: "detail-spec" }, [it.spec]));
    children.push(el("div", { class: "detail-fields" }, [
      el("div", { class: "detail-field" }, [
        el("span", { class: "field-label" }, ["Status"]),
        el("span", { class: "field-value" }, [it.status]),
      ]),
      ...(it.riskTier
        ? [el("div", { class: "detail-field" }, [
            el("span", { class: "field-label" }, ["Risk"]),
            el("span", { class: "field-value" }, [it.riskTier]),
          ])]
        : []),
      ...(it.agentId
        ? [el("div", { class: "detail-field" }, [
            el("span", { class: "field-label" }, ["Agent"]),
            el("span", { class: "field-value" }, [it.agentId]),
          ])]
        : []),
      ...(it.branch
        ? [el("div", { class: "detail-field" }, [
            el("span", { class: "field-label" }, ["Branch"]),
            el("span", { class: "field-value" }, [it.branch]),
          ])]
        : []),
    ]));
  } else {
    if (it.intent) children.push(el("p", { class: "detail-intent" }, [it.intent]));
    children.push(el("div", { class: "detail-fields" }, [
      el("div", { class: "detail-field" }, [
        el("span", { class: "field-label" }, ["Status"]),
        el("span", { class: "field-value" }, [it.status]),
      ]),
      ...(it.error
        ? [el("div", { class: "detail-field" }, [
            el("span", { class: "field-label" }, ["Error"]),
            el("span", { class: "field-value" }, [it.error]),
          ])]
        : []),
    ]));
  }

  return el("div", { class: "work-item-detail" }, children);
}

function itemCard(it: WorkItem, apiFetch?: ApiFetch): HTMLElement {
  const detailBody = buildDetailBody(it);

  const details = el("details", { class: `card work-item ${it.kind}` }, [
    el("summary", {}, [
      el("h3", {}, [
        el("span", { class: `kind-tag ${it.kind}` }, [it.kind === "bigtask" ? "Plan" : "Task"]),
        it.title,
      ]),
      it.subtitle ? el("p", { class: "meta" }, [it.subtitle]) : "",
    ]),
    detailBody,
  ]);

  if (apiFetch && it.kind === "task") {
    let loaded = false;
    details.addEventListener("toggle", () => {
      if (!(details as HTMLDetailsElement).open || loaded) return;
      loaded = true;
      const commentsSection = el("div", { class: "detail-comments" }, [
        el("p", { class: "loading" }, ["Loading comments…"]),
      ]);
      detailBody.append(commentsSection);
      apiFetch<Comment[]>("GET", `/api/tasks/${it.id}/comments`)
        .then((comments) => {
          commentsSection.replaceChildren(
            ...(comments.length === 0
              ? [el("p", { class: "no-comments" }, ["No comments."])]
              : comments.map((c) =>
                  el("div", { class: "comment" }, [
                    el("span", { class: "comment-author" }, [c.authorId]),
                    el("p", { class: "comment-body" }, [c.body]),
                  ]),
                )),
          );
        })
        .catch(() => {
          commentsSection.replaceChildren(el("p", { class: "error" }, ["Failed to load comments."]));
        });
    });
  }

  return details;
}

export function renderWorkGroups(items: WorkItem[], apiFetch?: ApiFetch): HTMLElement {
  const container = el("div", { class: "work-groups" });
  const groups = groupWorkItems(items);
  if (groups.length === 0) {
    container.append(el("div", { class: "empty" }, ["Nothing here yet — no tasks or plans. 📋"]));
    return container;
  }
  for (const group of groups) {
    container.append(
      el("details", { class: `work-group ${statusAccent(group.state)}`, open: !TERMINAL.has(group.state) }, [
        el("summary", {}, [group.state, el("span", { class: "badge count" }, [String(group.items.length)])]),
        ...group.items.map((it) => itemCard(it, apiFetch)),
      ]),
    );
  }
  return container;
}
